import {
  REQUEST_CREATE_USER,
  REQUEST_DELETE_USER,
  REQUEST_FETCH_USERS,
  REQUEST_INITIALIZE_CONFIRMATION,
  REQUEST_INITIALIZE_RECONFIRMATION,
  REQUEST_REGISTER,
  REQUEST_RESET_PASSWORD,
  REQUEST_SIGN_IN,
  REQUEST_SIGN_OUT,
  REQUEST_UPDATE_PASSWORD,
  REQUEST_UPDATE_USER,
  REQUEST_VERIFY_SESSION,
} from 'actions/actionTypes';
import * as AppAction from 'actions/appActions';
import * as SurveyAction from 'actions/backend/engagementSurvey/resultActions';
import * as UiAction from 'actions/backend/uiActions';
import * as Action from 'actions/backend/userActions';
import * as FlashMessageAction from 'actions/flashMessageActions';
import * as API from 'apis/backend';
import { push } from 'connected-react-router';
import { all, call, fork, put, select, take } from 'redux-saga/effects';
import { UserInputTypes } from 'types/backend/user';
import { ResponseType, RootState } from 'types/index';

// Selector
const appSelector = (state: RootState) => state.app;
const backendCompanySelector = (state: RootState) => state.backend.company;

function* runRequestVerifySession() {
  const { rootPath } = yield select((state) => state.app);
  const { payload, error, newXCSRFToken } = yield call(API.requestVerifySession, rootPath);

  if (payload && !error) {
    yield put(AppAction.setXCSRFToken(newXCSRFToken));

    if (payload.isAuthenticated) {
      yield put(Action.successAuthentication(payload));
    } else {
      yield put(Action.failureAuthentication());
    }
  } else {
    yield put(Action.failureVerifySession());
  }
}

function* handleRequestVerifySession() {
  while (true) {
    yield take(REQUEST_VERIFY_SESSION);
    yield fork(runRequestVerifySession);
  }
}

function* runRequestSignIn() {
  const { rootPath } = yield select((state) => state.app);
  const { signInEmail, signInPassword, isSessionExtended } = yield select(
    (state) => state.backend.user,
  );
  const { xCSRFToken } = yield select((state) => state.app);

  const { payload, error, newXCSRFToken } = yield call(
    API.requestSignIn,
    rootPath,
    signInEmail,
    signInPassword,
    isSessionExtended,
    xCSRFToken,
  );

  if (payload && !error) {
    yield put(AppAction.setXCSRFToken(newXCSRFToken));
    yield put(Action.successSignIn(payload));
  } else {
    if (error.base)
      yield put(
        FlashMessageAction.addFlashMessage({
          type: 'error',
          message: '入力内容に不備があるため、ログインできません。',
        }),
      );
    yield put(Action.failureSignIn());
  }
}

function* handleRequestSignIn() {
  while (true) {
    yield take(REQUEST_SIGN_IN);
    yield fork(runRequestSignIn);
  }
}

function* runRequestSignOut() {
  const { rootPath } = yield select((state) => state.app);
  const { payload, error, newXCSRFToken } = yield call(API.requestSignOut, rootPath);

  if (payload && !error) {
    yield put(AppAction.setXCSRFToken(newXCSRFToken));
    yield put(Action.successSignOut());
    yield put(SurveyAction.resetSurveyState());
  } else {
    yield put(Action.failureSignOut());
  }
}

function* handleRequestSignOut() {
  while (true) {
    yield take(REQUEST_SIGN_OUT);
    yield fork(runRequestSignOut);
  }
}

function* runRequestResetPassword() {
  const { rootPath } = yield select((state) => state.app);
  const { passwordResetEmail } = yield select((state) => state.backend.user);
  const { xCSRFToken } = yield select((state) => state.app);
  const { payload, error, newXCSRFToken } = yield call(
    API.requestResetPassword,
    rootPath,
    passwordResetEmail,
    xCSRFToken,
  );

  if (payload && !error) {
    yield put(AppAction.setXCSRFToken(newXCSRFToken));
    yield put(Action.successResetPassword());
    yield put(UiAction.toggleResetPasswordView(true));
  } else {
    yield put(Action.failureResetPassword());
  }
}

function* handleRequestResetPassword() {
  while (true) {
    yield take(REQUEST_RESET_PASSWORD);
    yield fork(runRequestResetPassword);
  }
}

function* runRequestUpdatePassword() {
  const { rootPath } = yield select((state) => state.app);
  const { password, passwordConfirmation } = yield select((state) => state.backend.user);
  const { xCSRFToken } = yield select((state) => state.app);
  const { payload, error, newXCSRFToken } = yield call(
    API.requestUpdatePassword,
    rootPath,
    password,
    passwordConfirmation,
    xCSRFToken,
  );

  if (payload && !error) {
    yield put(AppAction.setXCSRFToken(newXCSRFToken));
    yield put(Action.successUpdatePassword(payload));
    yield put(
      FlashMessageAction.addFlashMessage({
        type: 'success',
        message: 'パスワードの再設定が完了しました。',
      }),
    );
  } else {
    yield put(Action.failureUpdatePassword(error));
  }
}

function* handleRequestUpdatePassword() {
  while (true) {
    yield take(REQUEST_UPDATE_PASSWORD);
    yield fork(runRequestUpdatePassword);
  }
}

function* runRequestInitializeConfirmation() {
  const { rootPath, xCSRFToken } = yield select((state) => state.app);
  const { payload, error, newXCSRFToken } = yield call(
    API.requestInitializeConfirmation,
    rootPath,
    xCSRFToken,
  );

  if (payload && !error) {
    yield put(AppAction.setXCSRFToken(newXCSRFToken));
    yield put(Action.successInitializeConfirmation(payload));
  } else {
    if (error.email && error.email.length) {
      yield put(
        FlashMessageAction.addFlashMessage({
          type: 'error',
          message: error.email[0],
        }),
      );
    }
    yield put(Action.failureInitializeConfirmation());
    if (rootPath === 'backyard') {
      yield put(push('/backyard/sign_in'));
    } else {
      yield put(push('/admin/sign_in'));
    }
  }
}

function* handleRequestInitializeConfirmation() {
  while (true) {
    yield take(REQUEST_INITIALIZE_CONFIRMATION);
    yield fork(runRequestInitializeConfirmation);
  }
}

function* runRequestInitializeReconfirmation() {
  const { rootPath, xCSRFToken }: ReturnType<typeof appSelector> = yield select(appSelector);
  const { payload, error, newXCSRFToken } = yield call(
    API.requestInitializeReconfirmation,
    rootPath,
    xCSRFToken,
  );
  if (payload && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successInitializeReconfirmation(payload)),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'success',
          message: 'メールアドレスの変更が完了しました。',
        }),
      ),
    ]);
  } else {
    yield all([
      put(
        FlashMessageAction.addFlashMessage({
          type: 'error',
          message: 'メールアドレスの変更に失敗しました。',
        }),
      ),
      put(Action.failureInitializeReconfirmation()),
    ]);
  }
  yield put(push('/admin/sign_in'));
}

function* handleRequestInitializeReconfirmation() {
  while (true) {
    yield take(REQUEST_INITIALIZE_RECONFIRMATION);
    yield fork(runRequestInitializeReconfirmation);
  }
}

function* runRequestRegister() {
  const { rootPath } = yield select((state) => state.app);
  const { password, passwordConfirmation, isAgreedPrivacyPolicy } = yield select(
    (state) => state.backend.user,
  );
  const { xCSRFToken } = yield select((state) => state.app);

  const { payload, error, newXCSRFToken } = yield call(
    API.requestRegister,
    rootPath,
    password,
    passwordConfirmation,
    isAgreedPrivacyPolicy,
    xCSRFToken,
  );

  if (payload && !error) {
    yield put(AppAction.setXCSRFToken(newXCSRFToken));
    yield put(Action.successRegister(payload));
  } else {
    yield put(Action.failureRegister(error));
  }
}

function* handleRequestRegister() {
  while (true) {
    yield take(REQUEST_REGISTER);
    yield fork(runRequestRegister);
  }
}

function* runRequestFetchUsers(pageNumber: number) {
  const { rootPath } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);
  const { payload, error, newXCSRFToken, dataSize } = yield call(
    API.requestFetchUsers,
    rootPath,
    currentCompany,
    pageNumber,
  );
  if (payload && !error) {
    yield put(AppAction.setXCSRFToken(newXCSRFToken));
    yield put(Action.successFetchUsers(payload, dataSize.size, pageNumber));
  } else {
    yield put(Action.failureFetchUsers());
  }
}

function* handleRequestFetchUsers() {
  while (true) {
    const { pageNumber } = yield take(REQUEST_FETCH_USERS);
    yield fork(runRequestFetchUsers, pageNumber);
  }
}

function* runRequestCreateUser(params: UserInputTypes) {
  const { rootPath, xCSRFToken } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);

  const { payload, error, newXCSRFToken } = yield call(
    API.requestCreateUser,
    rootPath,
    currentCompany,
    params,
    xCSRFToken,
  );

  if (payload && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successCreateUser(payload)),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'success',
          message: '担当者を登録しました。',
        }),
      ),
    ]);
  } else {
    yield all([
      put(Action.failureCreateUser()),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'error',
          message: '担当者の登録に失敗しました。',
        }),
      ),
    ]);
  }
}

function* handleRequestCreateUser() {
  while (true) {
    const { params } = yield take(REQUEST_CREATE_USER);
    yield fork(runRequestCreateUser, params);
  }
}

function* runRequestUpdateUser(params: UserInputTypes) {
  const { rootPath, xCSRFToken } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);

  const { payload, error, newXCSRFToken } = yield call(
    API.requestUpdateUser,
    rootPath,
    currentCompany,
    params,
    xCSRFToken,
  );

  if (payload && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successUpdateUser(payload)),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'success',
          message: '編集を保存しました。',
        }),
      ),
    ]);
  } else {
    yield put(Action.failureUpdateUser());
  }
}

function* handleRequestUpdateUser() {
  while (true) {
    const { params } = yield take(REQUEST_UPDATE_USER);
    yield fork(runRequestUpdateUser, params);
  }
}

function* runRequestDeleteUser(id: string) {
  const { rootPath, xCSRFToken }: ReturnType<typeof appSelector> = yield select(appSelector);
  const { currentCompany }: ReturnType<typeof backendCompanySelector> =
    yield select(backendCompanySelector);

  if (!currentCompany) return;

  const { payload, error, newXCSRFToken }: ResponseType<'success'> = yield call(
    API.requestDeleteUser,
    rootPath,
    currentCompany,
    id,
    xCSRFToken,
  );

  if (payload === 'success' && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successDeleteUser(id)),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'success',
          message: '担当者を削除しました。',
        }),
      ),
    ]);
  } else {
    yield all([
      put(Action.failureDeleteUser()),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'error',
          message: '担当者の削除に失敗しました。',
        }),
      ),
    ]);
  }
}

function* handleRequestDeleteUser() {
  while (true) {
    const { id } = yield take(REQUEST_DELETE_USER);
    yield fork(runRequestDeleteUser, id);
  }
}

export default function* userSaga() {
  yield fork(handleRequestVerifySession);
  yield fork(handleRequestSignIn);
  yield fork(handleRequestSignOut);
  yield fork(handleRequestResetPassword);
  yield fork(handleRequestUpdatePassword);
  yield fork(handleRequestInitializeConfirmation);
  yield fork(handleRequestInitializeReconfirmation);
  yield fork(handleRequestRegister);
  yield fork(handleRequestFetchUsers);
  yield fork(handleRequestCreateUser);
  yield fork(handleRequestUpdateUser);
  yield fork(handleRequestDeleteUser);
}
